import { useEffect, useMemo, useRef } from 'react';
import { hasPendingRiskCoverage } from '../../../utils/positionReadiness';
import { callBackend } from '../../../utils/api';
import {
    buildRiskOverviewScopeKey,
    createRiskOverviewSnapshotPublicationTransaction,
} from '../riskOverviewResource';

const DEFAULT_POLL_INTERVAL_MS = 15_000;
const DEFAULT_MAX_POLL_INTERVAL_MS = 120_000;
const DEFAULT_JITTER_RATIO = 0.2;
const MIN_POLL_INTERVAL_MS = 250;
const MAX_BACKOFF_EXPONENT = 8;
const FAILURE_REPORT_THRESHOLDS = new Set([1, 2, 4, 8]);
const POLL_UNAVAILABLE_MESSAGE = 'Risk readiness refresh is temporarily unavailable.';

export function getRiskPollingDelay({
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxPollIntervalMs = DEFAULT_MAX_POLL_INTERVAL_MS,
    consecutiveFailures = 0,
    jitterRatio = DEFAULT_JITTER_RATIO,
    random = Math.random,
} = {}) {
    const base = Math.max(MIN_POLL_INTERVAL_MS, Number(pollIntervalMs) || DEFAULT_POLL_INTERVAL_MS);
    const cap = Math.max(base, Number(maxPollIntervalMs) || DEFAULT_MAX_POLL_INTERVAL_MS);
    const exponent = Math.min(Math.max(0, consecutiveFailures), MAX_BACKOFF_EXPONENT);
    const backoff = Math.min(cap, base * (2 ** exponent));
    const boundedJitter = Math.min(Math.max(Number(jitterRatio) || 0, 0), 0.5);
    const randomValue = Math.min(Math.max(Number(random()) || 0, 0), 1);
    return Math.min(
        cap,
        Math.max(MIN_POLL_INTERVAL_MS, Math.round(
            backoff * (1 + ((randomValue * 2) - 1) * boundedJitter),
        )),
    );
}

const reportPollingDiagnostic = (event, details) => {
    console.warn('Risk readiness polling diagnostic.', { event, ...details });
};

const normalizeVersion = (value) => (
    typeof value === 'string' ? value.trim() : ''
);

function parseReadinessProjection(result, expectedPortfolioId) {
    const nextCoverage = result?.risk_calculation_coverage;
    const countFields = [
        'total_positions',
        'included_count',
        'pending_count',
        'excluded_count',
        'failed_count',
    ];
    const hasValidCounts = countFields.every((field) => (
        Number.isInteger(nextCoverage?.[field]) && nextCoverage[field] >= 0
    ));
    const coverageVersion = normalizeVersion(nextCoverage?.coverage_version);
    const snapshotToken = normalizeVersion(nextCoverage?.snapshot_token);
    const riskContextOverride = {
        factorModel: normalizeVersion(result?.factor_model),
        factorDataModel: normalizeVersion(result?.factor_data_model),
        factorSnapshotDate: normalizeVersion(result?.factor_snapshot_date),
        betaSource: normalizeVersion(result?.beta_source),
        idioVolSource: normalizeVersion(result?.idio_vol_source),
    };
    const hasValidRiskContext = Object.values(riskContextOverride).every(Boolean)
        && /^\d{4}-\d{2}-\d{2}$/.test(riskContextOverride.factorSnapshotDate);
    if (
        result?.portfolio_id !== expectedPortfolioId
        || !hasValidCounts
        || typeof nextCoverage?.has_pending !== 'boolean'
        || nextCoverage.has_pending !== (nextCoverage.pending_count > 0)
        || nextCoverage.total_positions !== (
            nextCoverage.included_count
            + nextCoverage.pending_count
            + nextCoverage.excluded_count
            + nextCoverage.failed_count
        )
        || !coverageVersion
        || !snapshotToken
        || !hasValidRiskContext
    ) {
        throw new Error('Risk readiness response was invalid.');
    }
    return { nextCoverage, riskContextOverride, snapshotToken };
}

const contextValue = (value) => normalizeVersion(value).toLowerCase();

const isSameRiskContext = (scope, context) => (
    contextValue(scope?.factorModel) === contextValue(context?.factorModel)
    && contextValue(scope?.factorDataModel) === contextValue(context?.factorDataModel)
    && normalizeVersion(scope?.factorSnapshotDate) === normalizeVersion(context?.factorSnapshotDate)
    && contextValue(scope?.betaSource) === contextValue(context?.betaSource)
    && contextValue(scope?.idioVolSource) === contextValue(context?.idioVolSource)
);

/**
 * Polls a scalar readiness projection while the actual Risk Overview scope has
 * pending rows or a downstream Watchlist revision invalidates the visible
 * scope. A changed server snapshot token is handed to the existing position
 * and risk owners, then published only as one matching snapshot.
 */
export default function useRiskOverviewReadinessPolling({
    scope,
    coverage,
    fetchPositions,
    refreshRiskOverview,
    onEffectiveRiskContextChange,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxPollIntervalMs = DEFAULT_MAX_POLL_INTERVAL_MS,
    jitterRatio = DEFAULT_JITTER_RATIO,
    random = Math.random,
    refreshRequired = false,
    enabled = true,
}) {
    const generationRef = useRef(0);
    const failedSnapshotRetryRef = useRef(null);
    const pollScope = useMemo(() => ({
        userUid: scope?.userUid,
        portfolioId: scope?.portfolioId,
        portfolioRevision: scope?.portfolioRevision,
        targetKey: scope?.targetKey,
        factorModel: scope?.factorModel,
        factorDataModel: scope?.factorDataModel,
        factorSnapshotDate: scope?.factorSnapshotDate,
        betaSource: scope?.betaSource,
        idioVolSource: scope?.idioVolSource,
    }), [
        scope?.betaSource,
        scope?.factorDataModel,
        scope?.factorModel,
        scope?.factorSnapshotDate,
        scope?.idioVolSource,
        scope?.portfolioId,
        scope?.portfolioRevision,
        scope?.targetKey,
        scope?.userUid,
    ]);
    const scopeKey = useMemo(() => buildRiskOverviewScopeKey(pollScope), [pollScope]);
    const retainedRetry = failedSnapshotRetryRef.current?.scopeKey === scopeKey
        ? failedSnapshotRetryRef.current
        : null;
    const initialSnapshotToken = normalizeVersion(coverage?.snapshot_token)
        || retainedRetry?.snapshotToken
        || '';
    const scopeRef = useRef(pollScope);
    scopeRef.current = pollScope;
    const pending = refreshRequired
        || hasPendingRiskCoverage(coverage)
        || Boolean(retainedRetry);

    useEffect(() => {
        const generation = generationRef.current + 1;
        generationRef.current = generation;
        const activeScope = scopeRef.current;
        let timerId = null;
        let inFlight = false;
        let consecutiveFailures = 0;
        let lastSnapshotToken = initialSnapshotToken;
        let shouldContinue = pending;
        const isCurrent = () => generationRef.current === generation;
        const canPoll = () => (
            enabled
            && pending
            && activeScope.userUid
            && activeScope.portfolioId != null
            && typeof fetchPositions === 'function'
            && typeof refreshRiskOverview === 'function'
            && typeof onEffectiveRiskContextChange === 'function'
            && isCurrent()
        );

        const clearTimer = () => {
            if (timerId != null) {
                window.clearTimeout(timerId);
                timerId = null;
            }
        };

        const schedule = (failureCount = 0) => {
            clearTimer();
            if (!canPoll() || !shouldContinue || document.visibilityState === 'hidden') return;
            timerId = window.setTimeout(runPoll, getRiskPollingDelay({
                pollIntervalMs,
                maxPollIntervalMs,
                consecutiveFailures: failureCount,
                jitterRatio,
                random,
            }));
        };

        const runPoll = async () => {
            timerId = null;
            if (!canPoll() || inFlight) return;
            inFlight = true;
            try {
                const readinessResult = await callBackend(
                    'fetch_risk_overview_readiness',
                    'POST',
                    {
                        portfolio_id: activeScope.portfolioId,
                        factor_model: activeScope.factorModel,
                        factor_data_model: activeScope.factorDataModel,
                        beta_source: activeScope.betaSource,
                        idio_vol_source: activeScope.idioVolSource,
                    },
                );
                if (!isCurrent()) return;
                const {
                    nextCoverage,
                    riskContextOverride,
                    snapshotToken,
                } = parseReadinessProjection(readinessResult, activeScope.portfolioId);
                if (!isSameRiskContext(activeScope, riskContextOverride)) {
                    const transitionStarted = await onEffectiveRiskContextChange({
                        riskContext: riskContextOverride,
                    });
                    if (transitionStarted !== true) {
                        throw new Error('Risk context transition was not accepted.');
                    }
                    shouldContinue = false;
                    return;
                }
                const retryingFailedSnapshot = (
                    failedSnapshotRetryRef.current?.scopeKey === scopeKey
                );
                if (
                    refreshRequired
                    || snapshotToken !== lastSnapshotToken
                    || retryingFailedSnapshot
                ) {
                    const publication = createRiskOverviewSnapshotPublicationTransaction({
                        requiredPanels: ['positions', 'primary', 'group'],
                        scopeKey,
                        generation,
                        isCurrent,
                        expectedSnapshotToken: snapshotToken,
                    });
                    const stagePublicationState = publication.stage;
                    const positionsResult = await fetchPositions(activeScope.portfolioId, {
                        silent: true,
                        isCurrentRequest: isCurrent,
                        stagePublicationState,
                        riskContextOverride,
                        generation,
                        resourceScopeKey: scopeKey,
                    });
                    if (!isCurrent()) return;
                    if (positionsResult?.stale) {
                        publication.discard();
                        schedule(0);
                        return;
                    }
                    if (!Array.isArray(positionsResult?.positions)) {
                        throw new Error('Risk readiness positions response was invalid.');
                    }
                    await refreshRiskOverview({
                        idioVolSource: riskContextOverride.idioVolSource,
                        riskContextOverride,
                        isCurrentRequest: isCurrent,
                        stagePublicationState,
                        generation,
                        resourceScopeKey: scopeKey,
                    });
                    if (!isCurrent()) return;
                    const publishedFailure = publication.willPublishFailure();
                    if (publishedFailure) {
                        failedSnapshotRetryRef.current = {
                            scopeKey,
                            snapshotToken: lastSnapshotToken,
                        };
                    }
                    if (!publication.commit()) {
                        if (publishedFailure) {
                            failedSnapshotRetryRef.current = null;
                        }
                        schedule(0);
                        return;
                    }
                    if (publishedFailure) {
                        throw new Error('Risk readiness refresh did not produce a current snapshot.');
                    }
                    failedSnapshotRetryRef.current = null;
                    lastSnapshotToken = snapshotToken;
                }
                if (consecutiveFailures > 0) {
                    reportPollingDiagnostic('risk_readiness_poll_recovered', {
                        recoveredAfterFailures: consecutiveFailures,
                    });
                    consecutiveFailures = 0;
                }
                shouldContinue = hasPendingRiskCoverage(nextCoverage);
                schedule(0);
            } catch (_error) {
                if (!isCurrent()) return;
                consecutiveFailures += 1;
                if (FAILURE_REPORT_THRESHOLDS.has(consecutiveFailures)) {
                    reportPollingDiagnostic('risk_readiness_poll_unavailable', {
                        failureCount: consecutiveFailures,
                        errorCode: 'risk_readiness_poll_unavailable',
                        safeMessage: POLL_UNAVAILABLE_MESSAGE,
                    });
                }
                schedule(consecutiveFailures);
            } finally {
                inFlight = false;
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                clearTimer();
            } else if (!inFlight) {
                schedule(consecutiveFailures);
            }
        };

        if (canPoll()) {
            document.addEventListener('visibilitychange', handleVisibilityChange);
            if (refreshRequired && document.visibilityState !== 'hidden') {
                void runPoll();
            } else {
                schedule(0);
            }
        }

        return () => {
            generationRef.current += 1;
            clearTimer();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [
        enabled,
        fetchPositions,
        initialSnapshotToken,
        jitterRatio,
        maxPollIntervalMs,
        pending,
        pollIntervalMs,
        random,
        refreshRequired,
        refreshRiskOverview,
        onEffectiveRiskContextChange,
        scopeKey,
    ]);

}
